<template>
  <div :id="id" class="chartContainer" />
</template>

<script>
import { isEmpty, each, upperFirst } from 'lodash-es'
import rfConfigChartMixin from './rfConfigChartMixin'
import { mono as rfConstants } from '@/configuration/rfConfig'

export default {
  mixins: [rfConfigChartMixin],

  computed: {
    // Radios
    radio () {
      if (isEmpty(this.modem)) return []
      return [this.modem.radio_0, this.modem.radio_1]
    },
    // Multi SF channels
    multiSf () {
      if (isEmpty(this.modem)) return []
      return [this.modem.chan_multiSF_0, this.modem.chan_multiSF_1, this.modem.chan_multiSF_2, this.modem.chan_multiSF_3, this.modem.chan_multiSF_4, this.modem.chan_multiSF_5, this.modem.chan_multiSF_6, this.modem.chan_multiSF_7]
    },
    // Mono SF channel
    chan_Lora_std () {
      if (isEmpty(this.modem)) return {}
      return this.modem.chan_Lora_std
    },
    // Mono FSK channel, not required for now
    chan_FSK () {
      if (isEmpty(this.modem)) return {}
      return this.modem.chan_FSK
    },

    // Min frequency
    minFreq () {
      if (isEmpty(this.modem)) return 0
      return Math.min(this.radio[0].freq, this.radio[1].freq) / 1000000 - 1
    },

    // Max frequency
    maxFreq () {
      if (isEmpty(this.modem)) return 0
      return Math.max(this.radio[0].freq, this.radio[1].freq) / 1000000 + 1
    },

    // Graphic data
    dataProvider () {
      if (isEmpty(this.modem)) return []

      const rfConfig = []

      // Radios
      each(this.radio, (radio, i) => {
        if (radio.enable) {
          const freqbandLow = Number(radio.freq / 1000000) - (rfConstants.radioBandwidth / 2)
          const freqbandHigth = Number(radio.freq / 1000000) + (rfConstants.radioBandwidth / 2)
          rfConfig.push({
            t_text: 'Radio ' + i,
            freqband_low: freqbandLow - (rfConstants.multiSfBandwidth / 2),
            freqband_high: freqbandHigth + (rfConstants.multiSfBandwidth / 2),
            color: '#2196F3',
            text_balloon: upperFirst(this.$t('gc-rfConfigChartMono:radioBalloon', { freq: Number(radio.freq / 1000000) })),
            width: 0.7
          })
        }
      })

      // Multi SF channels
      each(this.multiSf, (multiSf, i) => {
        if (multiSf.enable) {
          const freq = this.radio[multiSf.radio].freq + multiSf.if
          const freqbandLow = Number(freq / 1000000) - (rfConstants.multiSfBandwidth / 2)
          const freqbandHigth = Number(freq / 1000000) + (rfConstants.multiSfBandwidth / 2)
          rfConfig.push({
            t_text: 'Multi SF' + i,
            frequency: freq,
            freqband_low: freqbandLow,
            freqband_high: freqbandHigth,
            color: '#4CAF50',
            text_balloon: upperFirst(this.$t('gc-rfConfigChartMono:multiSfBalloon', { radio: multiSf.radio, freq: Number(freq / 1000000) })),
            width: 0.4
          })
        }
      })

      // Mono SF channels
      if (this.chan_Lora_std && this.chan_Lora_std.enable) {
        const freq = this.radio[this.chan_Lora_std.radio].freq + this.chan_Lora_std.if
        const freqbandLow = Number(freq / 1000000) - (this.chan_Lora_std.bandwidth / 2 / 1000000)
        const freqbandHigth = Number(freq / 1000000) + (this.chan_Lora_std.bandwidth / 2 / 1000000)
        rfConfig.push({
          t_text: 'Lora MAC channel, ' + (this.chan_Lora_std.bandwidth / 1000) + 'kHz, SF' + this.chan_Lora_std.spread_factor,
          frequency: freq,
          freqband_low: freqbandLow,
          freqband_high: freqbandHigth,
          color: '#FAB619',
          text_balloon: upperFirst(this.$t('gc-rfConfigChartMono:monoSfBalloon', { radio: this.chan_Lora_std.radio, freq: Number(freq / 1000000) })),
          width: 0.4
        })
      }

      // Lora FSK channel
      if (this.chan_FSK && this.chan_FSK.enable) {
        const freq = this.radio[this.chan_FSK.radio].freq + this.chan_FSK.if
        const freqbandLow = Number(freq / 1000000) - (this.chan_FSK.bandwidth / 2 / 1000000)
        const freqbandHigth = Number(freq / 1000000) + (this.chan_FSK.bandwidth / 2 / 1000000)
        rfConfig.push({
          t_text: 'FSK ' + (this.chan_FSK.datarate / 1000) + 'kbps channel',
          frequency: freq,
          freqband_low: freqbandLow,
          freqband_high: freqbandHigth,
          color: '#6f42c1',
          text_balloon: upperFirst(this.$t('gc-rfConfigChartMono:fskBalloon', { radio: this.chan_FSK.radio, freq: Number(freq / 1000000) })),
          width: 0.4
        })
      }

      return [{
        empty_string: '',
        RF_config: rfConfig
      }]
    }
  }
}
</script>

<style scoped lang='scss'>
  .chartContainer {
    height: 200px;
  }
</style>
